import { useEffect, useMemo, useRef, useState } from 'react'
import ArrowIcon from '@/components/icons/ArrowIcon'
import { useGlobalStore } from '@/stores/globalStore'
import { useProjectStore } from '@/stores/projectStore'
import { useNavigationStore } from '@/stores/navigationStore'
import { useBuildingFloorsList } from './useBuildingFloorsList'
import {
  ArrowWrapper,
  Container,
  OptionItem,
  OptionsList,
  SelectToggle,
  SelectWrapper,
} from './FloorBar.styles'

const ChevronDown = ({ open }: { open?: boolean }) => (
  <ArrowWrapper $open={open}>
    <span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}>
      <ArrowIcon color="#666" />
    </span>
  </ArrowWrapper>
)

type FloorBarProps = {
  id?: string
}

export const FloorBar = ({ id = 'floorbar' }: FloorBarProps) => {
  const buildingFloorsList = useBuildingFloorsList()
  const activeLayer = useGlobalStore((state) => state.activeLayer)
  const setActiveLayer = useGlobalStore((state) => state.setActiveLayer)
  const nodes = useProjectStore((state) => state.nodes)
  const isRouteActive = useNavigationStore((state) => state.isRouteActive)

  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null)
  const [buildingOpen, setBuildingOpen] = useState(false)
  const [floorOpen, setFloorOpen] = useState(false)

  const buildingRef = useRef<HTMLDivElement>(null)
  const floorRef = useRef<HTMLDivElement>(null)
  const prevSelectedBuildingRef = useRef<string | null>(null)

  const floorOptions = useMemo(() => {
    if (!selectedBuilding) return []

    const building = buildingFloorsList.find(
      (item) => item.name === selectedBuilding,
    )

    return (building?.floorIds ?? []).map((layerId) => ({
      id: layerId,
      name: nodes.find((node) => node.id === layerId)?.name ?? String(layerId),
    }))
  }, [buildingFloorsList, nodes, selectedBuilding])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node

      if (
        buildingRef.current?.contains(target) ||
        floorRef.current?.contains(target)
      ) {
        return
      }

      setBuildingOpen(false)
      setFloorOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
    }
  }, [])

  useEffect(() => {
    if (isRouteActive) {
      setBuildingOpen(false)
      setFloorOpen(false)
    }
  }, [isRouteActive])

  useEffect(() => {
    if (!buildingFloorsList.length) return

    if (activeLayer == null) {
      if (!selectedBuilding) {
        setSelectedBuilding(buildingFloorsList[0].name)
      }
      return
    }

    const buildingForLayer = buildingFloorsList.find((building) =>
      building.floorIds.includes(Number(activeLayer)),
    )

    if (buildingForLayer) {
      if (selectedBuilding !== buildingForLayer.name) {
        setSelectedBuilding(buildingForLayer.name)
      }
    }
  }, [activeLayer, buildingFloorsList, selectedBuilding])

  useEffect(() => {
    if (!selectedBuilding) return

    const building = buildingFloorsList.find(
      (item) => item.name === selectedBuilding,
    )
    const floorIds = building?.floorIds ?? []

    if (!floorIds.length) return

    const buildingJustChanged =
      prevSelectedBuildingRef.current !== null &&
      prevSelectedBuildingRef.current !== selectedBuilding
    prevSelectedBuildingRef.current = selectedBuilding

    if (activeLayer == null) {
      setActiveLayer(floorIds[0])
      return
    }

    if (buildingJustChanged && !floorIds.includes(Number(activeLayer))) {
      setActiveLayer(floorIds[0])
    }
  }, [selectedBuilding, buildingFloorsList, activeLayer, setActiveLayer])

  if (!buildingFloorsList.length) return null

  const activeFloorName =
    floorOptions.find((floor) => floor.id === Number(activeLayer))?.name ?? '—'
  const routeLockTitle = 'Недоступно во время маршрута'

  return (
    <Container id={id}>
      <SelectWrapper ref={buildingRef}>
        <SelectToggle
          type="button"
          disabled={!buildingFloorsList.length || isRouteActive}
          title={isRouteActive ? routeLockTitle : undefined}
          onClick={() => {
            if (isRouteActive) return
            setBuildingOpen((open) => !open)
            setFloorOpen(false)
          }}
        >
          <span>{selectedBuilding ?? '—'}</span>
          <ChevronDown open={buildingOpen} />
        </SelectToggle>
        <OptionsList $open={buildingOpen}>
          {buildingFloorsList.map((building) => (
            <OptionItem
              key={building.name}
              onClick={() => {
                if (isRouteActive) return
                setSelectedBuilding(building.name)
                setBuildingOpen(false)
              }}
            >
              {building.name}
            </OptionItem>
          ))}
        </OptionsList>
      </SelectWrapper>

      <SelectWrapper ref={floorRef}>
        <SelectToggle
          type="button"
          disabled={!floorOptions.length || isRouteActive}
          title={isRouteActive ? routeLockTitle : undefined}
          onClick={() => {
            if (isRouteActive) return
            setFloorOpen((open) => !open)
            setBuildingOpen(false)
          }}
        >
          <span>{activeFloorName}</span>
          <ChevronDown open={floorOpen} />
        </SelectToggle>
        <OptionsList $open={floorOpen}>
          {floorOptions.map((floor) => (
            <OptionItem
              key={floor.id}
              onClick={() => {
                if (isRouteActive) return
                setActiveLayer(floor.id)
                setFloorOpen(false)
              }}
            >
              {floor.name}
            </OptionItem>
          ))}
        </OptionsList>
      </SelectWrapper>
    </Container>
  )
}
